import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
  UseInfiniteQueryOptions
} from '@tanstack/react-query';
import { ApiClient } from './client';
import { ApiError, RequestOptions, MutationConfig, QueryConfig } from './types';

/**
 * React Query hooks for API integration
 */

/**
 * useApiQuery - Fetch data using endpoint key
 *
 * @example
 * const { data, isLoading, refetch } = useApiQuery(
 *   apiClient,
 *   'users.list',  // endpoint key (becomes cache key)
 *   { params: { page: 1 } }
 * );
 */
export function useApiQuery<T = any>(
  client: ApiClient,
  endpointKey: string,
  options?: RequestOptions & QueryConfig
) {
  const endpoint = client.getEndpoint(endpointKey);
  const cacheKey = endpoint?.key || endpoint?.url || endpointKey;

  return useQuery<T, ApiError>({
    queryKey: [cacheKey, options?.params],
    queryFn: async () => {
      const response = await client.execute<T>(endpointKey, undefined, options);
      return response.data;
    },
    enabled: options?.enabled !== false,
    refetchOnMount: options?.refetchOnMount,
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime,
    retry: options?.retry,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  } as UseQueryOptions<T, ApiError>);
}

/**
 * useApiMutation - Mutate data (POST, PUT, PATCH, DELETE)
 *
 * @example
 * const createUser = useApiMutation(
 *   apiClient,
 *   'users.create',
 *   {
 *     onSuccess: (data) => console.log('Created:', data),
 *     invalidateQueries: ['users.list'] // Auto-refetch users list
 *   }
 * );
 *
 * createUser.mutate({ name: 'John' });
 */
export function useApiMutation<TData = any, TVariables = any>(
  client: ApiClient,
  endpointKey: string,
  config?: MutationConfig
) {
  const queryClient = useQueryClient();
  const endpoint = client.getEndpoint(endpointKey);
  const cacheKey = endpoint?.key || endpoint?.url || endpointKey;

  return useMutation<TData, ApiError, TVariables>({
    mutationKey: [cacheKey],
    mutationFn: async (variables: TVariables) => {
      const response = await client.execute<TData>(endpointKey, variables);
      return response.data;
    },
    onSuccess: (data: TData, variables: TVariables, context: unknown) => {
      // Invalidate specified queries
      if (config?.invalidateQueries) {
        config.invalidateQueries.forEach((key) => {
          const endpoint = client.getEndpoint(key);
          const queryKey = endpoint?.key || endpoint?.url || key;
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }

      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  } as UseMutationOptions<TData, ApiError, TVariables>);
}

/**
 * useApiInfiniteQuery - For pagination
 *
 * @example
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useApiInfiniteQuery(
 *   apiClient,
 *   'users.list',
 *   (pageParam) => ({ params: { page: pageParam } }),
 *   {
 *     getNextPageParam: (lastPage, pages) => {
 *       return lastPage.hasMore ? pages.length + 1 : undefined;
 *     }
 *   }
 * );
 */
export function useApiInfiniteQuery<T = any>(
  client: ApiClient,
  endpointKey: string,
  getOptions: (pageParam: unknown) => RequestOptions,
  config?: QueryConfig & {
    initialPageParam?: unknown;
    getNextPageParam?: (lastPage: any, allPages: any[]) => unknown;
    getPreviousPageParam?: (firstPage: any, allPages: any[]) => unknown;
  }
) {
  const endpoint = client.getEndpoint(endpointKey);
  const cacheKey = endpoint?.key || endpoint?.url || endpointKey;

  return useInfiniteQuery({
    queryKey: [cacheKey, 'infinite'],
    queryFn: async ({ pageParam }) => {
      const options = getOptions(pageParam);
      const response = await client.execute<T>(endpointKey, undefined, options);
      return response.data;
    },
    enabled: config?.enabled,
    staleTime: config?.staleTime,
    refetchOnMount: config?.refetchOnMount,
    refetchOnWindowFocus: config?.refetchOnWindowFocus,
    initialPageParam: config?.initialPageParam ?? 1,
    getNextPageParam: config?.getNextPageParam ?? ((lastPage: any) => undefined),
    getPreviousPageParam: config?.getPreviousPageParam,
  } as UseInfiniteQueryOptions<T, ApiError>);
}

/**
 * Manual refetch hook
 *
 * @example
 * const refetch = useRefetch(apiClient);
 *
 * refetch('users.list');              // Refetch specific query
 * refetch(['users.list', 'posts']);   // Refetch multiple
 */
export function useRefetch(client: ApiClient) {
  const queryClient = useQueryClient();

  return (keys: string | string[]) => {
    const keyArray = Array.isArray(keys) ? keys : [keys];

    keyArray.forEach((key) => {
      const endpoint = client.getEndpoint(key);
      const queryKey = endpoint?.key || endpoint?.url || key;
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    });
  };
}

/**
 * Get cached data hook
 *
 * @example
 * const getCachedData = useCache(apiClient);
 * const users = getCachedData('users.list');
 */
export function useCache(client: ApiClient) {
  const queryClient = useQueryClient();

  return <T = any>(key: string): T | undefined => {
    const endpoint = client.getEndpoint(key);
    const queryKey = endpoint?.key || endpoint?.url || key;
    return queryClient.getQueryData<T>([queryKey]);
  };
}

/**
 * Set cached data hook
 *
 * @example
 * const setCache = useSetCache(apiClient);
 * setCache('users.list', newData);
 */
export function useSetCache(client: ApiClient) {
  const queryClient = useQueryClient();

  return <T = any>(key: string, data: T) => {
    const endpoint = client.getEndpoint(key);
    const queryKey = endpoint?.key || endpoint?.url || key;
    queryClient.setQueryData([queryKey], data);
  };
}
